import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Upload, CheckCircle2, Loader2, FileText, ShieldCheck } from "lucide-react";

const SLOTS = [
  { type: "id_copy", label: "Certified ID copy", required: true },
  { type: "matric", label: "Matric certificate", required: false },
  { type: "results", label: "Results / latest results", required: false },
];

const OK_TYPES = ["application/pdf", "image/jpeg", "image/png", "image/webp"];

const toJpeg = (file) => new Promise((resolve) => {
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.onload = () => {
    const scale = Math.min(1, 2200 / Math.max(img.width, img.height));
    const c = document.createElement("canvas");
    c.width = Math.round(img.width * scale);
    c.height = Math.round(img.height * scale);
    c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
    URL.revokeObjectURL(url);
    c.toBlob((b) => resolve(b ? new File([b], (file.name || "photo").replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" }) : file), "image/jpeg", 0.85);
  };
  img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
  img.src = url;
});

const prepareFile = async (file) => {
  const isImage = (file.type || "").startsWith("image/") || /\.(heic|heif|jpe?g|png|webp)$/i.test(file.name || "");
  if (isImage && (file.size > 1.5 * 1024 * 1024 || !OK_TYPES.includes(file.type))) return toJpeg(file);
  return file;
};

export function DocumentsUpload({ onChange, disabled = false }) {
  const [docs, setDocs] = useState({});
  const [busy, setBusy] = useState(null);

  const refresh = () => api.get("/documents").then(({ data }) => {
    const map = {};
    data.documents.forEach((d) => { map[d.doc_type] = d; });
    setDocs(map);
    onChange && onChange(map);
  }).catch(() => {});

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const upload = async (type, file) => {
    if (!file) return;
    setBusy(type);
    try {
      const ready = await prepareFile(file);
      if (ready.size > 15 * 1024 * 1024) { toast.error("File too large (max 15MB). Try a smaller scan or a photo."); return; }
      const fd = new FormData();
      fd.append("doc_type", type);
      fd.append("file", ready, ready.name || "document");
      await api.post("/documents/upload", fd, { timeout: 120000 });
      toast.success("Uploaded.");
      await refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed — check your connection and try again.");
    } finally { setBusy(null); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid="documents-upload">
      <div className="flex items-center gap-2">
        <ShieldCheck className="w-4 h-4 text-[#F5C32C]" />
        <h3 className="font-display font-bold text-[#0B2447]">Supporting documents</h3>
      </div>
      <p className="text-sm text-slate-500 mt-1">Upload certified copies (PDF/JPG/PNG or a phone photo, max 15MB). Your ID copy plus a matric certificate or results are required. Stored securely (POPIA).</p>
      {disabled && <p className="mt-3 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="docs-consent-hint">Tick the POPIA consent box above to enable uploads.</p>}
      <div className="mt-4 space-y-3">
        {SLOTS.map((s) => {
          const uploaded = docs[s.type];
          return (
            <div key={s.type} className="flex items-center justify-between gap-3 border border-slate-100 rounded-lg p-3">
              <div className="flex items-center gap-2 text-sm min-w-0">
                {uploaded ? <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" /> : <FileText className="w-4 h-4 text-slate-400 shrink-0" />}
                <span className="text-[#0B2447] font-medium">{s.label}{s.required && <span className="text-red-500"> *</span>}</span>
                {uploaded && <span className="text-xs text-slate-400 truncate max-w-[160px]" data-testid={`doc-name-${s.type}`}>{uploaded.original_filename}</span>}
              </div>
              <label className={`cursor-pointer shrink-0 ${disabled || busy ? "opacity-40 pointer-events-none" : ""}`}>
                <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif,image/*,application/pdf" className="hidden" disabled={disabled}
                  data-testid={`doc-input-${s.type}`}
                  onChange={(e) => { upload(s.type, e.target.files?.[0]); e.target.value = ""; }} />
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium border border-[#0B2447]/30 text-[#0B2447] hover:bg-slate-50">
                  {busy === s.type ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {uploaded ? "Replace" : "Upload"}
                </span>
              </label>
            </div>
          );
        })}
      </div>
    </div>
  );
}
